import _get from 'lodash/get';
import _has from 'lodash/has';
import _isEmpty from 'lodash/isEmpty';
import _some from 'lodash/some';
import { visibleIfRegex, visibleIfContainsRegex } from './constants';

const getAssessmentJSON = (
  assessment,
  lastAnswer,
  isRetake,
  profile,
  insuranceQuestionNames,
) => {
  if (isRetake && lastAnswer) {
    const json = JSON.parse(assessment.json);
    const newPages = json.pages.map(page => {
      const newElements = page.elements.map(element => ({
        ...element,
        defaultValue: lastAnswer.result[element.name],
      }));

      const newPage = page;
      newPage.elements = newElements;
      return newPage;
    });

    json.pages = newPages;
    return removeGenderAgeIndustryQuestions(
      json,
      profile,
      insuranceQuestionNames,
    );
  }

  return removeGenderAgeIndustryQuestions(
    JSON.parse(assessment.json),
    profile,
    insuranceQuestionNames,
  );
};

const removeGenderAgeIndustryQuestions = (
  assessment,
  profile,
  insuranceQuestionNames = [],
) => {
  if (profile.isLoaded) {
    if (
      profile.dob ||
      profile.gender ||
      profile.industry ||
      !_isEmpty(profile.insurance)
    ) {
      const pages = assessment.pages.map(page => {
        const newElements = page.elements.map(el => {
          if (
            (profile.dob && el.name === 'ageRangeStandard') ||
            (profile.gender && el.name === 'genderStandard') ||
            (profile.industry && el.name === 'industryStandard') ||
            (!_isEmpty(profile.insurance) &&
              insuranceQuestionNames.includes(el.name))
          ) {
            const newElement = el;
            newElement.visible = false;

            return newElement;
          }

          const newElement = el;
          newElement.visible = true;

          return el;
        });

        if (newElements.filter(el => el.visible).length === 0) return null;

        return {
          ...page,
          elements: newElements,
        };
      });

      const newAssessment = assessment;
      newAssessment.pages = pages.filter(page => page !== null);

      return newAssessment;
    }
    return assessment;
  }
  return assessment;
};

const getDataFromResult = (result, name) => {
  const data = result[name];

  return data;
};

const getCleanResult = result => {
  const cleanResult = result;

  Object.keys(cleanResult).forEach(key => {
    if (key.includes('other-option/')) {
      const question = key.split('other-option/')[1];
      if (Array.isArray(cleanResult[question])) {
        cleanResult[question] = cleanResult[question]
          .concat(cleanResult[key])
          .filter(item => item !== 'other-option');
      } else {
        cleanResult[question] = cleanResult[key];
      }
      delete cleanResult[key];
    }
  });
  delete cleanResult.genderStandard;
  delete cleanResult.ageRangeStandard;
  delete cleanResult['genderStandard-Comment'];

  return cleanResult;
};

const convertToPattern = (fieldName, value) => {
  if (fieldName === 'dob') {
    switch (value) {
      case '0' || '1':
        return '[<18]';
      case '2' || '3':
        return '[18-24]';
      case '4':
        return '[25-34]';
      case '5':
        return '[35-44]';
      case '6':
        return '[45-54]';
      case '7':
        return '[55-64]';
      case '8':
        return '[>65]';
      case '9':
        return '[no-disclosure]';
      default:
        return value;
    }
  }
  if (fieldName === 'gender') {
    switch (value) {
      case '0':
        return 'Female';
      case '1':
        return 'Male';
      case '2':
        return 'Non-binary';
      case '3':
        return 'No-disclosure';
      default:
        return value;
    }
  }
  if (fieldName === 'industry') {
    switch (value) {
      case '1':
        return 'A student';
      case '2':
        return 'Working in healthcare';
      case '3':
        return 'Working in education or childcare';
      case '4':
        return 'Working in service, retail or gig economy';
      case '5':
        return 'Working in other private sector';
      case '6':
        return 'Working in other government';
      case '7':
        return 'Working in other non-profit';
      case '8':
        return 'Unemployed';
      case '9':
        return 'Retired';
      default:
        return value;
    }
  }
  if (fieldName === 'insurance') {
    const mapping = {
      '0': 'Medi-Cal or Medicaid',
      '1': 'Medicare',
      '2':
        'Private insurance (from your job or you bought through Covered California)',
      '3': 'Foster care or receiving child welfare services',
      '4': 'No insurance',
    };
    if (Array.isArray(value)) {
      return value.map(item => mapping[item] || item);
    }
    return mapping[value] || value;
  }
  return null;
};

const getNumberOfQuestions = data => {
  let len = 0;
  data.pages.forEach(item => {
    len += _get(item, 'elements', []).length;
  });

  return len;
};

const getQuestionVisibility = (question, answers, isPage = false) => {
  if (question.visible === false) return false;

  if (_has(question, 'visibleIf')) {
    const relatedAnswers = question.visibleIf.match(visibleIfRegex);
    const relatedAnswersExist = relatedAnswers.every(
      ans => _get(answers, ans.substring(1, ans.length - 1)) !== undefined,
    );
    const atLeastOneRelatedAnswerExist = relatedAnswers.some(
      ans => _get(answers, ans.substring(1, ans.length - 1)) !== undefined,
    );
    if (relatedAnswersExist || (isPage && atLeastOneRelatedAnswerExist)) {
      const pieces = question.visibleIf.split(/( or | and )/g);
      if (pieces.length === 1) {
        return getExpressionValue(pieces[0], answers);
      }
      const conditions = pieces.map(piece =>
        getExpressionValue(piece, answers),
      );
      let op = null;
      let result = null;
      for (let i = 0; i < conditions.length; i += 1) {
        if (i === 0) result = conditions[i];
        else if (typeof conditions[i] === 'string') {
          if (conditions[i].includes('or')) {
            op = 'or';
          } else if (conditions[i].includes('and')) {
            op = 'and';
          }
        } else if (typeof conditions[i] === 'boolean') {
          if (op === 'or') {
            result = result || conditions[i];
            op = null;
          } else if (op === 'and') {
            result = result && conditions[i];
            op = null;
          }
        }
      }
      return result;
    }
    return isPage;
  }
  return true;
};

const getResultsVisibility = (expression, answers) => {
  if (expression === '') return false;

  const relatedAnswers = expression.match(visibleIfRegex);
  const relatedAnswersExist = relatedAnswers.every(
    ans => _get(answers, ans.substring(1, ans.length - 1)) !== undefined,
  );
  if (relatedAnswersExist) {
    const pieces = expression.split(/( or | and )/g);
    if (pieces.length === 1) {
      return getExpressionValue(pieces[0], answers);
    }
    const conditions = pieces.map(piece => getExpressionValue(piece, answers));
    let op = null;
    let result = null;
    for (let i = 0; i < conditions.length; i += 1) {
      if (i === 0) result = conditions[i];
      else if (typeof conditions[i] === 'string') {
        if (conditions[i].includes('or')) {
          op = 'or';
        } else if (conditions[i].includes('and')) {
          op = 'and';
        }
      } else if (typeof conditions[i] === 'boolean') {
        if (op === 'or') {
          result = result || conditions[i];
          op = null;
        } else if (op === 'and') {
          result = result && conditions[i];
          op = null;
        }
      }
    }
    return result;
  }
  return false;
};

function getExpressionValue(expression, answers) {
  if (expression.includes(' or ') || expression.includes(' and '))
    return expression;

  const relatedAnswers = expression.match(visibleIfRegex);
  const match = expression.match(/(\[.*\])/g);
  if (match && match.length > 0) {
    const possibleValues = match[0]
      .substring(1, match[0].length - 1)
      .replace(/"/g, '')
      .split(',');
    return _some(possibleValues, value =>
      answers[
        relatedAnswers[0].substring(1, relatedAnswers[0].length - 1)
      ].includes(value),
    );
  }

  if (expression.includes('>=')) {
    return (
      relatedAnswers.reduce(
        (acc, value) =>
          acc + Number(_get(answers, value.substring(1, value.length - 1))),
        0,
      ) >= expression.split(' >= ')[1].replace(/["']/g, '')
    );
  }
  if (expression.includes('<=')) {
    return (
      relatedAnswers.reduce(
        (acc, value) =>
          acc + Number(_get(answers, value.substring(1, value.length - 1))),
        0,
      ) <= expression.split(' <= ')[1].replace(/["']/g, '')
    );
  }
  if (expression.includes('=')) {
    let expressionValue = expression.split(' = ')[1];
    if (expressionValue === 'true') {
      expressionValue = '1';
    } else if (expressionValue === 'false') {
      expressionValue = '0';
    }
    return (
      relatedAnswers.reduce(
        (acc, value) =>
          acc + Number(_get(answers, value.substring(1, value.length - 1))),
        0,
      ) === Number(expressionValue.replace(/["']/g, ''))
    );
  }
  if (expression.includes('<>')) {
    let expressionValue = expression.split(' <> ')[1];
    if (expressionValue === 'true') {
      expressionValue = '1';
    } else if (expressionValue === 'false') {
      expressionValue = '0';
    }
    return (
      relatedAnswers.reduce(
        (acc, value) =>
          acc + Number(_get(answers, value.substring(1, value.length - 1))),
        0,
      ) !== Number(expressionValue.replace(/["']/g, ''))
    );
  }
  if (expression.includes('>')) {
    return (
      relatedAnswers.reduce(
        (acc, value) =>
          acc + Number(_get(answers, value.substring(1, value.length - 1))),
        0,
      ) > expression.split(' > ')[1].replace(/["']/g, '')
    );
  }
  if (expression.includes('<')) {
    return (
      relatedAnswers.reduce(
        (acc, value) =>
          acc + Number(_get(answers, value.substring(1, value.length - 1))),
        0,
      ) < expression.split(' < ')[1].replace(/["']/g, '')
    );
  }
  if (visibleIfContainsRegex.test(expression)) {
    const elements = expression
      .replace(/[()[\]"'\s]/g, '')
      .split('contains')[1]
      .split(',');
    return answers[relatedAnswers[0]].includes(elements);
  }
  return true;
}

export {
  getAssessmentJSON,
  removeGenderAgeIndustryQuestions,
  getDataFromResult,
  getCleanResult,
  convertToPattern,
  getNumberOfQuestions,
  getQuestionVisibility,
  getResultsVisibility,
};
